import { Box } from '@mui/material';
import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { OrderData } from '../../api';
import { UNASSIGNED_ID } from '../views/dragndrop/DragDropTypes';

export type GeneratedResource = {
  id: string;
  name: string;
  index: number;
  schedulerConfigId: string;
  workshopColor?: string;
};

export enum ResourceDropState {
  VALID = 'valid',
  INVALID = 'invalid',
  UNAVAILABLE = 'unavailable',
  NEUTRAL = 'neutral',
}

type ResourceColumnProps = {
  resource: GeneratedResource | null;
  orders: Array<OrderData & { css: any }>;
  columnWidth: number;
  leftPosition: number;
  openOrder: (event: any, order: OrderData) => void;
  dropState?: ResourceDropState;
  previewSpan?: number; // Number of resources this assignment would span
};

export const ResourceColumn = ({
  resource,
  orders,
  columnWidth,
  leftPosition,
  openOrder,
  dropState = ResourceDropState.NEUTRAL,
  previewSpan = 1,
}: ResourceColumnProps) => {
  const { setNodeRef, isOver } = useDroppable({
    id: resource?.id || UNASSIGNED_ID,
  });

  const getBackgroundColor = () => {
    // Base workshop color with very light opacity
    const baseColor = resource?.workshopColor ? `${resource.workshopColor}18` : 'transparent';
    if (isOver) {
      switch (dropState) {
        case ResourceDropState.VALID:
          return 'rgba(76, 175, 80, 0.35)'; // More visible green for valid
        case ResourceDropState.INVALID:
          return 'rgba(244, 67, 54, 0.2)'; // Red for invalid
        case ResourceDropState.UNAVAILABLE:
          return 'rgba(244, 67, 54, 0.25)'; // Red for unavailable (same as invalid)
        default:
          return 'rgba(76, 175, 80, 0.2)'; // Light green default
      }
    }

    // Subtle hints when not hovering
    switch (dropState) {
      case ResourceDropState.VALID:
        return 'rgba(76, 175, 80, 0.15)';
      case ResourceDropState.INVALID:
        return 'rgba(244, 67, 54, 0.05)';
      case ResourceDropState.UNAVAILABLE:
        return 'rgba(244, 67, 54, 0.08)'; // Red hint for unavailable
      default:
        return baseColor; // Use base workshop color when no specific state
    }
  };

  // Get border style for multi-resource preview
  const getBorderStyle = () => {
    if (isOver && previewSpan > 1) {
      const color = dropState === ResourceDropState.VALID ? '#4CAF50' : '#F44336';
      return `2px dashed ${color}`;
    }
    return undefined;
  };

  return (
    <Box
      id={'mmm'}
      ref={setNodeRef}
      sx={{
        width: `${columnWidth * previewSpan}%`, // Expand width for multi-resource preview
        left: `${leftPosition}%`,
        height: 50 * 17,
        position: 'absolute',
        borderRight: '1px solid #e0e0e0',
        backgroundColor: getBackgroundColor(),
        borderInlineStart: '1px solid #E6E6E6',
        border: getBorderStyle(),
        opacity: dropState === ResourceDropState.UNAVAILABLE || dropState === ResourceDropState.INVALID ? 0.8 : 1,
        cursor:
          dropState === ResourceDropState.UNAVAILABLE || dropState === ResourceDropState.INVALID
            ? 'not-allowed'
            : 'default',
        transition: 'all 0.2s ease-in-out',
      }}>
      {/* This column is just for drop zones - orders are rendered separately */}
    </Box>
  );
};
